import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { theme } from '../constants/theme';
import {
  bureauOptions,
  mediumOptions,
  titelOptions,
  doelgroepOptions,
  uitingOptions,
  getDefaultOptions
} from '../utils/staticData';
import useFirestore from '../hooks/useFirestore';
import { normalizeText } from '../utils/textNormalization';

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.colors.background,
    fontSize: '14px',
    '& fieldset': {
      borderColor: theme.colors.border,
    },
    '&:hover fieldset': {
      borderColor: theme.colors.primary,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.colors.primary,
    },
    '&.Mui-error fieldset': {
      borderColor: theme.colors.error,
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '14px',
    padding: '12px 14px',
  },
  '& .MuiInputLabel-root': {
    color: theme.colors.text,
    fontSize: '14px',
    '&.Mui-focused': {
      color: theme.colors.primary,
    },
  },
}));

const StyledFormControl = styled(FormControl)(() => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.colors.background,
    fontSize: '14px',
    '& fieldset': {
      borderColor: theme.colors.border,
    },
    '&:hover fieldset': {
      borderColor: theme.colors.primary,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.colors.primary,
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '14px',
    padding: '12px 14px',
  },
  '& .MuiSelect-select': {
    fontSize: '14px',
    padding: '12px 14px',
  },
  '& .MuiInputLabel-root': {
    color: theme.colors.text,
    fontSize: '14px',
    '&.Mui-focused': {
      color: theme.colors.primary,
    },
  },
  '& .MuiMenuItem-root': {
    fontSize: '14px',
  },
}));

const StyledButton = styled(Button)(() => ({
  backgroundColor: theme.colors.primary,
  color: '#fff',
  textTransform: 'none',
  fontWeight: 600,
  padding: '8px 24px',
  '&:hover': {
    backgroundColor: '#c73e24',
  },
}));

const DatePickerWrapper = styled('div')(() => ({
  '& .react-datepicker-wrapper': {
    width: '100%',
  },
  '& .react-datepicker__input-container input': {
    width: '100%',
    padding: '12px 14px',
    fontSize: '14px',
    fontFamily: 'inherit',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px',
    backgroundColor: theme.colors.background,
    '&:focus': {
      outline: 'none',
      borderColor: theme.colors.primary,
      borderWidth: '2px',
    },
    '&.error': {
      borderColor: theme.colors.error,
      backgroundColor: '#ffeeee',
    }
  },
}));

const DetailsDialog = ({ 
  isOpen, 
  onClose, 
  onSave, 
  item, 
  mode = 'Add',
  fieldConfig,
  projectId,
  language = 'nl'
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [suggestionsByField, setSuggestionsByField] = useState({});
  const { loadEntries } = useFirestore();

  // Text translations
  const text = {
    nl: {
      addTitle: 'Voeg regel toe',
      editTitle: 'Pas regel aan',
      batchEditTitle: 'Selectie Aanpassen',
      batchCopyTitle: 'Selectie Kopieëren',
      detailsTitle: 'Details',
      save: 'Opslaan',
      update: 'Aanpassen',
      copy: 'Kopieer',
      cancel: 'Annuleren',
      differsPerRow: '-- verschilt per regel --',
      requiredField: 'is een verplicht veld',
      invalidUrl: 'URL bevat ongeldige tekens'
    },
    en: {
      addTitle: 'Add row',
      editTitle: 'Edit row',
      batchEditTitle: 'Batch Edit',
      batchCopyTitle: 'Batch Copy',
      detailsTitle: 'Details',
      save: 'Save',
      update: 'Update',
      copy: 'Copy',
      cancel: 'Cancel',
      differsPerRow: '-- differs per row --',
      requiredField: 'is a required field',
      invalidUrl: 'URL contains invalid characters'
    }
  };

  const t = text[language] || text.nl;
  const placeholder = t.differsPerRow;

  // Helper function to check if all items have the same value for a field
  const getFieldValue = (items, field) => {
    if (!Array.isArray(items) || items.length === 0) {
      return '';
    }

    const firstValue = items[0][field];
    const allSame = items.every(item => item[field] === firstValue);
    
    return allSame ? (firstValue || '') : placeholder;
  };

  // Helper function to parse date from string
  const parseDate = (dateValue) => {
    if (!dateValue) return new Date();
    
    if (typeof dateValue === 'string') {
      const parts = dateValue.split('/');
      if (parts.length === 3) {
        return new Date(parts[2], parts[0] - 1, parts[1]);
      }
    } else if (dateValue instanceof Date) {
      return dateValue;
    }
    
    return new Date();
  };

  // Helper function to get date value for batch mode
  const getDateValue = (items) => {
    if (!Array.isArray(items) || items.length === 0) {
      return new Date();
    }

    const firstDate = parseDate(items[0].Datum);
    const allSame = items.every(item => {
      const itemDate = parseDate(item.Datum);
      return itemDate.getTime() === firstDate.getTime();
    });
    
    return allSame ? firstDate : null; // null represents "different values"
  };

  // Initialize form data
  useEffect(() => {
    if (!isOpen) return;

    const isBatchMode = Array.isArray(item);
    const newFormData = {};

    // Always include all standard fields
    const allFieldNames = ['Bureau', 'Medium', 'Titel', 'Doelgroep', 'Uiting', 'Linknaam', 'Campagne', 'Datum', 'Url'];
    
    // Get enabled fields from config
    const enabledFieldNames = fieldConfig && fieldConfig.fields 
      ? Object.keys(fieldConfig.fields).filter(name => fieldConfig.fields[name].enabled)
      : allFieldNames;

    allFieldNames.forEach(fieldName => {
      const fieldType = fieldConfig?.fields?.[fieldName]?.fieldType || 
        (fieldName === 'Datum' ? 'date' : 'text');
      const isEnabled = enabledFieldNames.includes(fieldName);

      if (item) {
        if (isBatchMode) {
          if (fieldType === 'date') {
            newFormData[fieldName] = getDateValue(item);
          } else {
            newFormData[fieldName] = getFieldValue(item, fieldName);
          }
        } else {
          if (fieldType === 'date') {
            newFormData[fieldName] = parseDate(item[fieldName]);
          } else {
            // For disabled fields, use empty string as default if no value
            newFormData[fieldName] = item[fieldName] !== undefined ? item[fieldName] : '';
          }
        }
      } else {
        // New entry defaults
        if (fieldType === 'date') {
          newFormData[fieldName] = new Date();
        } else {
          newFormData[fieldName] = '';
        }
      }
    });

    setFormData(newFormData);
    setErrors({});
  }, [isOpen, item, fieldConfig]);

  // Load autosuggest options for text fields with autosuggest enabled
  useEffect(() => {
    const loadSuggestions = async () => {
      if (!isOpen || !projectId || !fieldConfig || !fieldConfig.fields) {
        setSuggestionsByField({});
        return;
      }

      const autosuggestFields = Object.entries(fieldConfig.fields)
        .filter(([, cfg]) => cfg.enabled && cfg.fieldType === 'text' && cfg.autosuggest)
        .map(([name]) => name);

      if (autosuggestFields.length === 0) {
        setSuggestionsByField({});
        return;
      }

      try {
        const items = await loadEntries(projectId);
        const map = {};
        autosuggestFields.forEach((fname) => {
          const values = items
            .map((it) => it[fname])
            .filter((v) => typeof v === 'string' && v.trim() !== '');
          map[fname] = Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
        });
        setSuggestionsByField(map);
      } catch (e) {
        // Fallback to empty suggestions on error
        setSuggestionsByField({});
      }
    };

    loadSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, projectId, fieldConfig?.fields]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleInputChange = (field, value) => {
    // Never normalize URL field
    if (field === 'Url') {
      handleChange(field, value);
      return;
    }

    const flags = fieldConfig?.textNormalization || {};
    const isTextField = fieldConfig?.fields?.[field]?.fieldType === 'text';

    // Fallback for when no config present yet: keep legacy behavior for common text fields
    const isLikelyTextWithoutConfig = !fieldConfig?.fields && (field === 'Linknaam' || field === 'Campagne');

    if (isTextField || isLikelyTextWithoutConfig) {
      handleChange(field, normalizeText(value || '', flags));
      return;
    }

    handleChange(field, value);
  };

  const validate = () => {
    const newErrors = {};
    const isBatchMode = mode === 'BatchEdit' || mode === 'BatchCopy';

    if (!fieldConfig || !fieldConfig.fields) {
      // Default validation without config
      if (!formData.Campagne) newErrors.Campagne = `Campagne ${t.requiredField}`;
      if (!formData.Datum) newErrors.Datum = `Datum ${t.requiredField}`;
      return Object.keys(newErrors).length === 0;
    }

    // Validate based on field config
    Object.keys(fieldConfig.fields).forEach(fieldName => {
      const config = fieldConfig.fields[fieldName];
      
      if (!config.enabled) return;
      if (!config.required && !isBatchMode) return;

      const value = formData[fieldName];

      // In batch mode, skip validation for placeholder values
      if (isBatchMode && value === placeholder) return;
      if (isBatchMode && value === null) return; // date placeholder

      // Required field validation (only in non-batch mode)
      if (!isBatchMode && config.required) {
        if (config.fieldType === 'date') {
          if (!value) {
            newErrors[fieldName] = `${config.displayName} ${t.requiredField}`;
          }
        } else {
          if (!value || value.trim() === '') {
            newErrors[fieldName] = `${config.displayName} ${t.requiredField}`;
          }
        }
      }

      // URL validation
      if (fieldName === 'Url' && value && value !== placeholder) {
        // eslint-disable-next-line no-useless-escape
        const urlRegex = /[^A-Za-z0-9\/\-\._~:\?#\[\]@!\$\&\'\(\)\*\+\,\;\=\`\.%]+/gi;
        if (urlRegex.test(value)) {
          newErrors[fieldName] = t.invalidUrl;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Format data for save - include ALL fields with default values for disabled ones
    const dataToSave = {};
    
    // Standard field names that should always exist
    const standardFields = ['Bureau', 'Medium', 'Titel', 'Doelgroep', 'Uiting', 'Linknaam', 'Campagne', 'Datum', 'Url'];
    
    standardFields.forEach(fieldName => {
      const value = formData[fieldName];
      const fieldType = fieldConfig?.fields?.[fieldName]?.fieldType || 
        (fieldName === 'Datum' ? 'date' : 'text');
      const isEnabled = fieldConfig?.fields?.[fieldName]?.enabled !== false;

      if (fieldType === 'date') {
        if (value === null) {
          dataToSave[fieldName] = placeholder;
        } else if (value) {
          dataToSave[fieldName] = `${(value.getMonth() + 1).toString().padStart(2, '0')}/${value.getDate().toString().padStart(2, '0')}/${value.getFullYear()}`;
        } else {
          // Default date if undefined
          const now = new Date();
          dataToSave[fieldName] = `${(now.getMonth() + 1).toString().padStart(2, '0')}/${now.getDate().toString().padStart(2, '0')}/${now.getFullYear()}`;
        }
      } else {
        // Use value if present, otherwise empty string (important for disabled fields)
        dataToSave[fieldName] = value !== undefined ? value : '';
      }
    });

    onSave(dataToSave);
  };

  const getTitle = () => {
    switch (mode) {
      case 'Add': return t.addTitle;
      case 'Edit': return t.editTitle;
      case 'BatchEdit': return t.batchEditTitle;
      case 'BatchCopy': return t.batchCopyTitle;
      default: return t.detailsTitle;
    }
  };

  const getSubmitButtonText = () => {
    switch (mode) {
      case 'Add': return t.save;
      case 'Edit': return t.update;
      case 'BatchEdit': return t.save;
      case 'BatchCopy': return t.copy;
      default: return t.save;
    }
  };

  // Get enabled fields sorted by order
  const getEnabledFields = () => {
    if (!fieldConfig || !fieldConfig.fields) {
      // Return default fields if no config
      return [
        { name: 'Campagne', config: { displayName: 'Campagne', fieldType: 'text', options: [], required: true, gridOrder: 1 }},
        { name: 'Datum', config: { displayName: 'Datum', fieldType: 'date', options: [], required: true, gridOrder: 2 }},
        { name: 'Url', config: { displayName: 'URL', fieldType: 'text', options: [], required: false, gridOrder: 3 }},
        { name: 'Bureau', config: { displayName: 'Bureau', fieldType: 'select', options: bureauOptions, required: true, gridOrder: 4 }},
        { name: 'Medium', config: { displayName: 'Type netwerk', fieldType: 'select', options: mediumOptions, required: true, gridOrder: 5 }},
        { name: 'Titel', config: { displayName: 'Naam netwerk', fieldType: 'select', options: titelOptions, required: true, gridOrder: 6 }},
        { name: 'Doelgroep', config: { displayName: 'Doelgroep', fieldType: 'select', options: doelgroepOptions, required: true, gridOrder: 7 }},
        { name: 'Uiting', config: { displayName: 'Type uiting', fieldType: 'select', options: uitingOptions, required: true, gridOrder: 8 }},
        { name: 'Linknaam', config: { displayName: 'Naam uiting', fieldType: 'text', options: [], required: true, gridOrder: 9 }},
      ];
    }

    return Object.keys(fieldConfig.fields)
      .filter(fieldName => fieldConfig.fields[fieldName].enabled)
      .map(fieldName => ({
        name: fieldName,
        config: fieldConfig.fields[fieldName]
      }))
      .sort((a, b) => a.config.gridOrder - b.config.gridOrder);
  };

  // Render a single field
  const renderField = (fieldName, config, isFirst = false) => {
    const value = formData[fieldName] || (config.fieldType === 'date' ? new Date() : '');
    const error = errors[fieldName];
    const shouldAutoFocus = isFirst && mode === 'Add';

    if (config.fieldType === 'date') {
      return (
        <DatePickerWrapper key={fieldName}>
          <InputLabel sx={{ mb: 0.5, fontSize: '0.875rem', color: theme.colors.text }}>
            {config.displayName}
          </InputLabel>
          {value === null ? (
            <input
              type="text"
              value={placeholder}
              readOnly
              style={{
                width: '100%',
                padding: '12px 14px',
                fontSize: '14px',
                fontFamily: 'inherit',
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '4px',
                backgroundColor: theme.colors.background,
                color: theme.colors.textSecondary,
              }}
              onClick={() => handleChange(fieldName, new Date())}
            />
          ) : (
            <DatePicker
              selected={value}
              onChange={(date) => handleChange(fieldName, date)}
              dateFormat="MM/dd/yyyy"
              className={error ? 'error' : ''}
              autoFocus={shouldAutoFocus}
            />
          )}
          {error && (
            <Box sx={{ color: theme.colors.error, fontSize: '0.75rem', mt: 0.5 }}>
              {error}
            </Box>
          )}
        </DatePickerWrapper>
      );
    }

    if (config.fieldType === 'select') {
      const options = config.options && config.options.length > 0 
        ? config.options 
        : getDefaultOptions(fieldName);

      return (
        <StyledFormControl key={fieldName} fullWidth error={!!error}>
          <InputLabel>{config.displayName}</InputLabel>
          <Select
            value={value}
            label={config.displayName}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            autoFocus={shouldAutoFocus}
          >
            {value === placeholder && (
              <MenuItem value={placeholder}>{placeholder}</MenuItem>
            )}
            {options.map((option) => (
              <MenuItem key={option.Id} value={option.Id}>
                {option.Name}
              </MenuItem>
            ))}
          </Select>
          {error && (
            <Box sx={{ color: theme.colors.error, fontSize: '0.75rem', mt: 0.5 }}>
              {error}
            </Box>
          )}
        </StyledFormControl>
      );
    }

    // Text field (with optional autosuggest)
    if (config.autosuggest) {
      const options = suggestionsByField[fieldName] || [];
      const isPlaceholder = value === placeholder;
      const inputVal = value || '';
      return (
        <Autocomplete
          key={fieldName}
          freeSolo
          options={options}
          filterOptions={(opts, params) => {
            const q = params.inputValue || '';
            if (q === placeholder) return [];
            if (q.trim().length < 2) return [];
            const ql = q.toLowerCase();
            return opts.filter(o => o.toLowerCase().includes(ql));
          }}
          inputValue={isPlaceholder ? placeholder : inputVal}
          onInputChange={(event, newInputValue) => {
            handleInputChange(fieldName, newInputValue || '');
          }}
          onChange={(event, newValue) => {
            if (typeof newValue === 'string') {
              handleInputChange(fieldName, newValue || '');
            }
          }}
          renderInput={(params) => (
            <StyledTextField
              {...params}
              fullWidth
              label={config.displayName}
              error={!!error}
              helperText={error}
              autoFocus={shouldAutoFocus}
              inputProps={{
                ...params.inputProps,
                maxLength: fieldName === 'Url' ? 200 : fieldName === 'Linknaam' ? 20 : 100
              }}
              InputProps={{
                ...params.InputProps,
                style: isPlaceholder
                  ? { color: theme.colors.textSecondary, fontStyle: 'italic' }
                  : {}
              }}
              onFocus={() => {
                if (isPlaceholder) {
                  handleChange(fieldName, '');
                }
              }}
            />
          )}
        />
      );
    }

    return (
      <StyledTextField
        key={fieldName}
        fullWidth
        label={config.displayName}
        value={value}
        onChange={(e) => handleInputChange(fieldName, e.target.value)}
        error={!!error}
        helperText={error}
        autoFocus={shouldAutoFocus}
        inputProps={{ maxLength: fieldName === 'Url' ? 200 : fieldName === 'Linknaam' ? 20 : 100 }}
        InputProps={{
          style: value === placeholder
            ? { color: theme.colors.textSecondary, fontStyle: 'italic' }
            : {}
        }}
        onFocus={() => {
          if (value === placeholder) {
            handleChange(fieldName, '');
          }
        }}
      />
    );
  };

  return (
    <Dialog 
      open={isOpen} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: `${theme.borderRadius.lg}px`,
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: `1px solid ${theme.colors.border}`,
        pb: 2
      }}>
        {getTitle()}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: theme.colors.textSecondary,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ paddingTop: '32px !important' }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {getEnabledFields().map(({ name, config }, index) => renderField(name, config, index === 0))}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, pt: 3, borderTop: `1px solid ${theme.colors.border}` }}>
        <Button onClick={onClose} sx={{ color: theme.colors.textSecondary }}>
          {t.cancel}
        </Button>
        <StyledButton onClick={handleSubmit}>
          {getSubmitButtonText()}
        </StyledButton>
      </DialogActions>
    </Dialog>
  );
};

export default DetailsDialog;
